import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, ShareIcon, XIcon } from "lucide-react"
import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
} from "react"
import { Button } from "@/primitives/button.tsx"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/primitives/dialog.tsx"
import { cn } from "@/primitives/utils"
import { useFullScreenTask } from "./full-screen-task.ts"
import {
  axisOf,
  followOf,
  positionOf,
  steppedImage,
  swipedTo,
  swipeOf,
  type Axis,
  type Swipe,
} from "./lightbox.ts"
import { canShareFiles, shareFile } from "./share.ts"
import { useBackDismiss } from "./use-back-dismiss.ts"
import { useOverlayForm } from "./use-overlay-form.ts"
import { fileLinkOf } from "./viewport.ts"
import { useViewport } from "./viewport.tsx"

/*
 * Images one at a time, large: a ticket's attachments, or an image in markdown. `index` is the image
 * shown, or none while it is closed; ← and → step through the others, going round at the ends.
 *
 * On the desk it is a dialog: the file's name, "2 of 5" and Download above it, arrows over it, each
 * image at its own size, never stretched, shrunk to fit a stage of one fixed height so the header
 * and the arrows stay put from a wide image to a tall one.
 *
 * On `compact` it fills the screen, on the near-black `lightbox` in both schemes: Close, the name
 * over "2 of 5", and Share along the top, the image filling the rest. A swipe across steps, never
 * round past either end; a swipe down closes, as does Back. Share hands the file itself to the
 * phone's share sheet. `fileOf` fetches the shown image's file as it shows, since the tap's
 * permission to share expires while a file downloads, and Share waits, dimmed, until it is in hand;
 * where the browser can't share a file, `fileOf` is not given, or fetching it failed, it is
 * Download. Whether it is the dialog or the full screen is chosen as it opens and kept while it is
 * open.
 */

export type LightboxImage = {
  /** Where the image is fetched from, for its `src`, and for its Download without `download`. */
  src: string
  /** Where Download saves the file from, when that isn't `src`: an address that serves it to be saved. */
  download?: string
  /** The file's name: the title over it, and the name it downloads or shares under. */
  name: string
}

export function Lightbox({
  images,
  index,
  onIndex,
  fileOf,
}: {
  images: readonly LightboxImage[]
  index: number | null
  onIndex: (index: number | null) => void
  /** The image's file, for the share sheet; without it a phone offers Download alone. */
  fileOf?: (image: LightboxImage) => Promise<File>
}) {
  const open = index !== null && images[index] !== undefined
  const { compact } = useOverlayForm({ open })
  return compact ? (
    <FullScreen images={images} index={open ? index : null} onIndex={onIndex} fileOf={fileOf} />
  ) : (
    <Centred images={images} index={open ? index : null} onIndex={onIndex} />
  )
}

/** Where an image's Download saves it from. */
const downloadOf = (image: LightboxImage) => image.download ?? image.src

/** The file behind a link that downloads it, opened outside the installed app's window. */
function DownloadLink({ image, className }: { image: LightboxImage; className?: string }) {
  const { standalone } = useViewport()
  return (
    <Button
      variant="ghost"
      size="sm"
      className={className}
      render={<a href={downloadOf(image)} download={image.name} {...fileLinkOf(standalone)} />}
      nativeButton={false}
    >
      <DownloadIcon />
      Download
    </Button>
  )
}

type FormProps = {
  images: readonly LightboxImage[]
  index: number | null
  onIndex: (index: number | null) => void
}

/** The desk's: a dialog, as it always was. */
function Centred({ images, index, onIndex }: FormProps) {
  const shown = index === null ? undefined : images[index]
  const step = (by: 1 | -1) => index !== null && onIndex(steppedImage(index, images.length, by))
  // The lightbox itself takes focus, so ← and → work at once and no button is lit on opening.
  const popup = useRef<HTMLDivElement>(null)
  return (
    <Dialog open={shown !== undefined} onOpenChange={(open) => !open && onIndex(null)}>
      <DialogContent
        ref={popup}
        initialFocus={popup}
        size="xl"
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") step(1)
          else if (event.key === "ArrowLeft") step(-1)
        }}
      >
        {shown && (
          <>
            <DialogHeader className="flex-row items-center gap-2">
              <DialogTitle className="min-w-0 flex-1 truncate">{shown.name}</DialogTitle>
              {images.length > 1 && (
                <span className="text-xs text-subtle-foreground tabular-nums">
                  {positionOf(index ?? 0, images.length)}
                </span>
              )}
              <DownloadLink image={shown} />
            </DialogHeader>
            <DialogBody className="relative grid h-160 place-items-center">
              <img
                src={shown.src}
                alt={shown.name}
                className="h-auto max-h-156 w-auto max-w-full rounded-md object-contain"
              />
              {images.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label="Previous image"
                    onClick={() => step(-1)}
                    className="absolute top-1/2 left-7 -translate-y-1/2"
                  >
                    <ChevronLeftIcon />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label="Next image"
                    onClick={() => step(1)}
                    className="absolute top-1/2 right-7 -translate-y-1/2"
                  >
                    <ChevronRightIcon />
                  </Button>
                </>
              )}
            </DialogBody>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** A finger on the image: where it went down, when, and which way it is going once that's known. */
type Drag = { pointer: number; x: number; y: number; at: number; axis?: Axis }

/** How long the images take to settle after a finger lifts, in ms: the `duration-200` below. */
const settling = 200

/** A control on the lightbox: light on the near-black, its press a light wash. */
const onLightbox =
  "text-lightbox-foreground hover:bg-lightbox-foreground/10 hover:text-lightbox-foreground focus-visible:ring-offset-lightbox"

/** A phone's: the whole screen, swiped across to step and down to close. */
function FullScreen({
  images,
  index,
  onIndex,
  fileOf,
}: FormProps & { fileOf: ((image: LightboxImage) => Promise<File>) | undefined }) {
  const open = index !== null
  const shown = index === null ? undefined : images[index]
  const close = () => onIndex(null)
  useBackDismiss(open, close)
  useFullScreenTask(open)

  const stage = useRef<HTMLDivElement>(null)
  const drag = useRef<Drag | null>(null)
  // Where the images stand off their place, following a finger or settling after it, and the swipe
  // that settling ends in. They move `instant`ly, with no easing, while they follow a finger and as
  // one image takes another's place; otherwise they ease.
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [instant, setInstant] = useState(false)
  const [settle, setSettle] = useState<Swipe | null>(null)
  // Once the images have settled, the swipe takes effect: the image it stepped to moves into the
  // middle place, where it already shows, without moving. A swipe down closes where it left off,
  // the backdrop fading on from as faint as the finger left it rather than back from black.
  const settled = useEffectEvent((swipe: Swipe) => {
    setSettle(null)
    if (swipe === "close") return close()
    if (index !== null) onIndex(swipedTo(index, images.length, swipe))
    setInstant(true)
    setOffset({ x: 0, y: 0 })
  })
  useEffect(() => {
    if (!settle) return
    const done = setTimeout(() => settled(settle), settling)
    return () => clearTimeout(done)
  }, [settle])
  // A new opening starts at rest, before it is painted.
  useLayoutEffect(() => {
    if (!open) return
    setSettle(null)
    setInstant(true)
    setOffset({ x: 0, y: 0 })
  }, [open])

  // The shown image's file, fetched as it shows, for Share to hand over within the tap that asked:
  // `fetching` until it is in hand, and none once fetching it failed, when Download stands in. Only
  // the shown image's is held, and none once it closes; showing an image again fetches it again.
  const sharing = fileOf !== undefined && canShareFiles()
  const [file, setFile] = useState<{ src: string; file: File | "fetching" | null }>()
  const fetchShown = useEffectEvent(() => (shown ? fileOf?.(shown) : undefined))
  const src = sharing ? shown?.src : undefined
  useEffect(() => {
    if (!src) {
      setFile(undefined)
      return
    }
    let current = true
    setFile({ src, file: "fetching" })
    fetchShown()?.then(
      (fetched) => current && setFile({ src, file: fetched }),
      () => current && setFile({ src, file: null }),
    )
    return () => {
      current = false
    }
  }, [src])
  const shareable = file?.src === shown?.src ? file?.file : "fetching"

  const { standalone } = useViewport()
  function save(image: LightboxImage) {
    const link = Object.assign(document.createElement("a"), {
      href: downloadOf(image),
      download: image.name,
      ...fileLinkOf(standalone),
    })
    link.click()
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (drag.current || settle) return
    drag.current = {
      pointer: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      at: event.timeStamp,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const from = drag.current
    if (!from || from.pointer !== event.pointerId || index === null) return
    const dx = event.clientX - from.x
    const dy = event.clientY - from.y
    from.axis ??= axisOf(dx, dy)
    if (!from.axis) return
    setInstant(true)
    setOffset(followOf({ axis: from.axis, dx, dy, index, count: images.length }))
  }
  function onPointerUp(event: PointerEvent<HTMLDivElement>) {
    const from = drag.current
    if (!from || from.pointer !== event.pointerId || index === null) return
    drag.current = null
    setInstant(false)
    const box = stage.current?.getBoundingClientRect()
    const swipe = from.axis
      ? swipeOf({
          axis: from.axis,
          dx: event.clientX - from.x,
          dy: event.clientY - from.y,
          ms: event.timeStamp - from.at,
          width: box?.width ?? window.innerWidth,
          height: box?.height ?? window.innerHeight,
        })
      : "stay"
    const to = swipedTo(index, images.length, swipe)
    if (swipe === "close") {
      setOffset({ x: 0, y: box?.height ?? window.innerHeight })
      setSettle("close")
    } else if (to !== index) {
      setOffset({ x: (to > index ? -1 : 1) * (box?.width ?? window.innerWidth), y: 0 })
      setSettle(swipe)
    } else {
      setOffset({ x: 0, y: 0 })
    }
  }
  function onPointerCancel(event: PointerEvent<HTMLDivElement>) {
    if (drag.current?.pointer !== event.pointerId) return
    drag.current = null
    setInstant(false)
    setOffset({ x: 0, y: 0 })
  }

  // Swiping down fades the near-black away, to the page beneath, and the controls with it.
  const fade = offset.y > 0 ? Math.max(0, 1 - offset.y / window.innerHeight) : 1
  const slots = shown
    ? [images[(index ?? 0) - 1], shown, images[(index ?? 0) + 1]].map((image, slot) => ({
        image,
        slot,
      }))
    : []

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && close()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-50 bg-lightbox data-open:animate-in data-open:fade-in-0 data-open:duration-200 data-closed:animate-out data-closed:fade-out-0 data-closed:duration-150",
            !instant && "transition-opacity duration-200 ease-snappy",
          )}
          style={{ opacity: fade }}
        />
        <DialogPrimitive.Popup
          data-lightbox=""
          initialFocus={stage}
          onKeyDown={(event) => {
            if (index === null) return
            if (event.key === "ArrowRight") onIndex(steppedImage(index, images.length, 1))
            else if (event.key === "ArrowLeft") onIndex(steppedImage(index, images.length, -1))
          }}
          className="fixed inset-0 z-50 flex flex-col pt-safe pb-safe text-lightbox-foreground outline-none data-open:animate-in data-open:fade-in-0 data-open:duration-200 data-closed:animate-out data-closed:fade-out-0 data-closed:duration-150"
        >
          {shown && (
            <>
              <div
                className={cn(
                  "flex h-14 shrink-0 items-center gap-2 px-2",
                  !instant && "transition-opacity duration-200 ease-snappy",
                )}
                style={{ opacity: fade }}
              >
                <DialogPrimitive.Close
                  render={<Button variant="ghost" size="icon" className={onLightbox} />}
                >
                  <XIcon />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
                <div className="min-w-0 flex-1 text-center">
                  <DialogPrimitive.Title className="truncate text-ui font-medium">
                    {shown.name}
                  </DialogPrimitive.Title>
                  {images.length > 1 && (
                    <p className="text-xs text-lightbox-foreground/70 tabular-nums">
                      {positionOf(index ?? 0, images.length)}
                    </p>
                  )}
                </div>
                {sharing && shareable !== null ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Share ${shown.name}`}
                    disabled={shareable === "fetching"}
                    aria-busy={shareable === "fetching"}
                    onClick={() =>
                      shareable &&
                      shareable !== "fetching" &&
                      void shareFile(shareable).catch(() => save(shown))
                    }
                    className={onLightbox}
                  >
                    <ShareIcon />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Download ${shown.name}`}
                    render={
                      <a
                        href={downloadOf(shown)}
                        download={shown.name}
                        {...fileLinkOf(standalone)}
                      />
                    }
                    nativeButton={false}
                    className={onLightbox}
                  >
                    <DownloadIcon />
                  </Button>
                )}
              </div>
              <div
                ref={stage}
                tabIndex={-1}
                className="relative min-h-0 flex-1 touch-none overflow-hidden outline-none select-none"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
              >
                <div
                  className={cn(
                    "absolute inset-0 flex",
                    !instant && "transition-transform duration-200 ease-snappy",
                  )}
                  style={{
                    transform: `translate3d(calc(-100% + ${offset.x}px), ${offset.y}px, 0)`,
                  }}
                >
                  {slots.map(({ image, slot }) => (
                    <div
                      key={image?.src ?? `empty-${slot}`}
                      aria-hidden={slot !== 1}
                      className="grid h-full w-full shrink-0 place-items-center"
                    >
                      {image && (
                        <img
                          src={image.src}
                          alt={slot === 1 ? image.name : ""}
                          draggable={false}
                          className="max-h-full max-w-full object-contain"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
